/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { Form, FormInstance } from 'antd';

import { ValidationError } from '@/components/Forms/ValidationError';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { Flex } from '@/primitives/Flex';
import { OsIcon } from '@/primitives/Icons';
import { Input } from '@/primitives/Input';
import { Select } from '@/primitives/Select';
import { TextArea } from '@/primitives/TextArea';

import { FLEET_NAME_MAX_LENGTH, NewFleetFormFields } from './types';

export type FleetDetailsStepProps = {
  form: FormInstance<NewFleetFormFields>;
  isPending: boolean;
};

export function FleetDetailsStep({ form, isPending }: FleetDetailsStepProps) {
  const { t } = useMimicTranslation('fleets');

  return (
    <div className="new-fleet-form">
      <Form form={form} layout="vertical" validateTrigger="onBlur" style={{ width: '100%' }}>
        <Form.Item<NewFleetFormFields>
          label={t('wizard.form.name')}
          name="name"
          rules={[
            {
              required: true,
              message: <ValidationError>{t('wizard.form.nameRequired')}</ValidationError>,
            },
            {
              max: FLEET_NAME_MAX_LENGTH,
              message: (
                <ValidationError>{t('wizard.form.nameMaxLength', { max: FLEET_NAME_MAX_LENGTH })}</ValidationError>
              ),
            },
          ]}
        >
          <Input
            placeholder={t('wizard.form.namePlaceholder')}
            disabled={isPending}
            count={{ max: FLEET_NAME_MAX_LENGTH }}
            style={{ backgroundColor: '#ffffff' }}
          />
        </Form.Item>

        <Form.Item<NewFleetFormFields> label={t('wizard.form.description')} name="description">
          <TextArea
            allowClear
            rows={3}
            maxLength={280}
            count={{ show: true, max: 280 }}
            style={{ backgroundColor: '#ffffff' }}
          />
        </Form.Item>

        <Form.Item<NewFleetFormFields>
          label={t('wizard.form.operatingSystem')}
          name="operatingSystem"
          rules={[
            {
              required: true,
              message: <ValidationError>{t('wizard.form.operatingSystemRequired')}</ValidationError>,
            },
          ]}
        >
          <Select
            placeholder={t('wizard.form.operatingSystemPlaceholder')}
            disabled={isPending}
            options={[
              {
                label: (
                  <Flex align="center" gap="xs">
                    <OsIcon name="windows" />
                    {t('windows')}
                  </Flex>
                ),
                value: 'windows',
              },
              {
                label: (
                  <Flex align="center" gap="xs">
                    <OsIcon name="linux" />
                    {t('linux')}
                  </Flex>
                ),
                value: 'linux',
              },
            ]}
          />
        </Form.Item>
      </Form>
    </div>
  );
}
